import { TaskStatus } from "client/openapi";
import { formatDate } from "components/Forms/ResponseDialog";
import type { FieldValue } from "components/FieldValueInput";
import { namesInWords } from "components/MessageTemplates/TemplatePreviewParts";
import {
  Facets,
  facetsOf,
  kindOf,
  KindTiming,
  kindTiming,
  statusLabel,
  TASK_FINISHED,
  TASK_STATUS_CHANGED,
} from "./kinds";
import { cardTaskStep, inAndBox, isSubjects, Lane, stagesOf } from "./model";
import {
  columnById,
  columnByKey,
  columnLabel,
  CrmField,
  fieldByRef,
  formName,
  kindInfo,
  nameOf,
  ReferenceData,
  ROLE_OF_ACCOUNT_ROLE,
  subjectName,
  templateById,
} from "./reference";
import type {
  ActivityAction,
  ApproveAllResult,
  ActivityPillKind,
  ActivityRow,
  ActivityStatus,
  Approvers,
  AssignChoice,
  AssignTo,
  AssignTutorConfig,
  Gate,
  Position,
  SendTo,
  Step,
  StepAction,
  SubjectsMode,
  TimePosition,
  Trigger,
  TriggerCard,
  TriggerFilter,
} from "./types";

/*
 * Plain English for automations, so every screen says things the same way.
 * Sentences name people; they never say she, he, her or his.
 */

// ── Small words ───────────────────────────────────────────────────────────

export function plural(n: number, one: string, many = `${one}s`) {
  return `${n} ${n === 1 ? one : many}`;
}

export function capitalize(text: string) {
  return text ? text.charAt(0).toUpperCase() + text.slice(1) : text;
}

export function lowerFirst(text: string) {
  return text ? text.charAt(0).toLowerCase() + text.slice(1) : text;
}

export function ordinal(n: number) {
  if (n % 100 >= 11 && n % 100 <= 13) return `${n}th`;
  return `${n}${["th", "st", "nd", "rd"][n % 10] ?? "th"}`;
}

export function money(n: unknown) {
  return (Number(n) || 0).toLocaleString("en-US", {
    style: "currency",
    currency: "USD",
  });
}

/** "10:00" as "10:00 AM". */
function clock(hhmm: string) {
  const [h, m] = (hhmm || "10:00").split(":").map(Number);
  const suffix = h >= 12 ? "PM" : "AM";
  return `${h % 12 || 12}:${String(m || 0).padStart(2, "0")} ${suffix}`;
}

const WEEKDAYS = [
  "Monday",
  "Tuesday",
  "Wednesday",
  "Thursday",
  "Friday",
  "Saturday",
  "Sunday",
];

// ── Labels ────────────────────────────────────────────────────────────────

export const ACTION_LABELS: Record<StepAction, string> = {
  message: "Send a message",
  task: "Create a task",
  crm_move: "Move to a CRM column",
  crm_field: "Update a CRM field",
  close_task: "Close a task",
  assign_tutor: "Assign a tutor",
  add_note: "Add a note",
};

export const PILL_LABELS: Record<ActivityPillKind, string> = {
  task_created: "Task created",
  message_sent: "Message sent",
  column_moved: "Column moved",
  field_updated: "Field updated",
  task_closed: "Task closed",
  tutor_assigned: "Tutor assigned",
  note_added: "Note added",
  skipped: "Skipped",
  failed: "Failed",
};

/** Done's Status pill. */
export const STATUS_LABELS: Record<ActivityStatus, string> = {
  completed: "Completed",
  failed: "Failed",
  skipped: "Skipped",
};

/** What approving each kind of item does, for Approve all's confirm. */
const APPROVE_WORDS: Record<ActivityAction, string> = {
  message: "messages go out",
  task: "tasks are made",
  crm_move: "people move to a CRM column",
  crm_field: "CRM fields are updated",
  close_task: "tasks are closed",
  assign_tutor: "tutors are assigned",
  add_note: "notes are added",
  wait: "",
};

/** What approving these kinds of item does: "Messages go out and tasks are made." */
export function approveAllWords(kinds: ActivityAction[]) {
  const words = namesInWords(kinds.map((kind) => APPROVE_WORDS[kind]));
  return words ? `${capitalize(words)}.` : "";
}

/** What Approve all leaves out, for a person to look at one by one. */
export function leftOutWords(count: number) {
  return `${count} with blank details left out`;
}

/** How Approve all ended: "33 approved and 1 skipped, since it no longer applies." */
export function approvedWords({
  approved,
  skipped,
  failed,
  taken,
}: ApproveAllResult) {
  const parts = [`${approved} approved`];
  if (skipped)
    parts.push(
      `${skipped} skipped, since ${
        skipped === 1 ? "it no longer applies" : "they no longer apply"
      }`
    );
  if (failed) parts.push(`${failed} failed`);
  if (taken) parts.push(`${taken} already answered or changed`);
  return `${namesInWords(parts)}.`;
}

/**
 * Done's Action pill, by the kind of step: what it did when it was done
 * ("Message sent"), else only its kind ("Message"), as a skipped or failed
 * step did not do it.
 */
export const ACTION_PILL_WORDS: Record<
  ActivityAction,
  { done: string; kind: string }
> = {
  message: { done: PILL_LABELS.message_sent, kind: "Message" },
  task: { done: PILL_LABELS.task_created, kind: "Task" },
  crm_move: { done: PILL_LABELS.column_moved, kind: "Column move" },
  crm_field: { done: PILL_LABELS.field_updated, kind: "Field update" },
  close_task: { done: PILL_LABELS.task_closed, kind: "Close task" },
  assign_tutor: { done: PILL_LABELS.tutor_assigned, kind: "Assign tutor" },
  add_note: { done: PILL_LABELS.note_added, kind: "Note" },
  wait: { done: "Wait", kind: "Wait" },
};

/**
 * Why a Done row failed or was skipped: the backend's own reason, or who
 * skipped it (Skip in Waiting for approval, or Not now on its card).
 */
export function reasonWords(ref: ReferenceData, row: ActivityRow) {
  if (row.skip === "not_now") {
    const who = !row.decidedBy
      ? "Someone"
      : row.decidedBy === ref.me
      ? "You"
      : nameOf(ref, row.decidedBy);
    return `${who} skipped it.`;
  }
  return row.detail || row.pillLabel || PILL_LABELS[row.pill];
}

export const COPY = {
  fallbackNote: (ref: ReferenceData) =>
    `Nobody fit, so ${ref.orgName}'s admins got it.`,
};

/** Who can approve, as a card's foot says it. */
export const APPROVER_WORDS: Record<Approvers, string> = {
  admins: "Admins can approve",
  tutors: "Tutors can approve",
  both: "Admins or tutors can approve",
};

/** Who an item waits for, in a sentence: "an admin", "a tutor". */
export const APPROVER_ONE: Record<Approvers, string> = {
  admins: "an admin",
  tutors: "a tutor",
  both: "an admin or a tutor",
};

// ── Variables ─────────────────────────────────────────────────────────────

const VARIABLE = /\{\{\s*([A-Za-z][A-Za-z0-9_.]*)\s*(?:\|([^{}]*))?\}\}/g;

/** A piece of text with {{variables}}: plain words, or a variable with its label. */
export type TextToken =
  | { kind: "text"; text: string }
  | { kind: "variable"; key: string; label: string; fallback: string | null };

/** "student's full name" for {{student.full_name}}, from the catalog's own labels. */
export function variableLabel(ref: ReferenceData, key: string) {
  for (const group of ref.catalog.variables) {
    const found = group.variables.find((v) => v.key === key);
    if (found) return `${lowerFirst(group.name)}'s ${lowerFirst(found.label)}`;
  }
  return key;
}

/** Text split into words and variables, for showing variables as chips. */
export function variableTokens(ref: ReferenceData, text: string): TextToken[] {
  const tokens: TextToken[] = [];
  let last = 0;
  String(text || "").replace(
    VARIABLE,
    (match, key: string, fallback: string | undefined, at: number) => {
      if (at > last) tokens.push({ kind: "text", text: text.slice(last, at) });
      tokens.push({
        kind: "variable",
        key,
        label: variableLabel(ref, key),
        fallback: fallback?.trim() || null,
      });
      last = at + match.length;
      return match;
    }
  );
  if (last < text.length) tokens.push({ kind: "text", text: text.slice(last) });
  return tokens;
}

/**
 * A task name in words: "Intake call with the parent" for "Intake call
 * with {{parent.full_name}}".
 */
export function nameInWords(ref: ReferenceData, text: string) {
  const words = String(text || "").replace(VARIABLE, (match, key: string) => {
    const [group, field] = key.split(".");
    if (
      ["student", "parent", "tutor"].includes(group) &&
      /name$/.test(field || "")
    )
      return `the ${group}`;
    if (key === "org.name") return ref.orgName;
    if (key === "task.name") return "the task";
    if (key === "form.name") return "the form";
    return variableLabel(ref, key);
  });
  return capitalize(words.replace(/\s+/g, " ").trim());
}

// ── Triggers ──────────────────────────────────────────────────────────────

function dateFieldWords(ref: ReferenceData, field: unknown) {
  const value = String(field ?? "");
  if (value.endsWith(".birth_date")) return `${value.split(".")[0]}'s birthday`;
  const choice = ref.catalog.date_fields.find((f) => f.value === value);
  if (!choice) return "a date field";
  const owner =
    (choice.person_type && ROLE_OF_ACCOUNT_ROLE[choice.person_type]) ||
    "student";
  return `${owner}'s ${choice.label}`;
}

function cadenceWords(ref: ReferenceData, o: Record<string, any>) {
  const at = clock(o.time);
  // "Every week", "Every 2 weeks"; a schedule without the number is every one.
  const every = (n: unknown, unit: string) =>
    Number(n) > 1 ? `Every ${Number(n)} ${unit}s` : `Every ${unit}`;
  const when =
    o.frequency === "monthly"
      ? `${every(o.every_months, "month")} on the ${ordinal(
          Number(o.day_of_month) || 1
        )} at ${at}`
      : `${every(o.every_weeks, "week")} on ${
          WEEKDAYS[Number(o.weekday) || 0]
        } at ${at}`;
  const who =
    o.audience === "column"
      ? `everyone in ${columnLabel(ref, o.column)}`
      : `all active ${o.audience || "students"}`;
  return `${when}, to ${who}`;
}

/** "Name contains “Check in”", "Made by Welcome new families", "About Lily Chen". */
export function filterWords(
  ref: ReferenceData,
  filter: TriggerFilter,
  automationName?: (id: string) => string | null
) {
  switch (filter.field) {
    case "name_contains":
      return `Name contains “${filter.value.trim()}”`;
    case "made_by": {
      if (!filter.value || filter.value === "any")
        return "Made by any automation";
      const name = automationName?.(filter.value);
      return name
        ? `Made by ${name}`
        : "Made by an automation that was deleted";
    }
    case "assigned_to":
      return `Assigned to ${nameOf(ref, filter.value)}`;
    case "about":
      return `About ${nameOf(ref, filter.value)}`;
  }
}

/** One filter inside "When a task … is finished": "whose name contains “Check in”", "about Lily Chen". */
function filterPhrase(
  ref: ReferenceData,
  f: TriggerFilter,
  automationName?: (id: string) => string | null
) {
  switch (f.field) {
    case "name_contains":
      return `whose name contains “${f.value.trim()}”`;
    case "made_by": {
      const name =
        f.value && f.value !== "any" ? automationName?.(f.value) : null;
      return f.value && f.value !== "any"
        ? `made by ${name ?? "an automation that was deleted"}`
        : "made by an automation";
    }
    case "assigned_to":
      return `assigned to ${nameOf(ref, f.value)}`;
    case "about":
      return `about ${nameOf(ref, f.value)}`;
  }
}

/** One filter as a clause: "its name contains “Check in”", "Welcome new families made it". */
function filterClause(
  ref: ReferenceData,
  f: TriggerFilter,
  automationName?: (id: string) => string | null
) {
  switch (f.field) {
    case "name_contains":
      return `its name contains “${f.value.trim()}”`;
    case "made_by": {
      if (!f.value || f.value === "any") return "an automation made it";
      return `${
        automationName?.(f.value) ?? "an automation that was deleted"
      } made it`;
    }
    case "assigned_to":
      return `it is assigned to ${nameOf(ref, f.value)}`;
    case "about":
      return `it is about ${nameOf(ref, f.value)}`;
  }
}

/** The filters that narrow anything (an empty Name contains does not). */
export function liveFilters(filters: TriggerFilter[]) {
  return (filters || []).filter((f) =>
    f.field === "name_contains"
      ? f.value.trim() !== ""
      : f.field === "made_by" || !!f.value
  );
}

/**
 * A kind of trigger in words, without any automation's settings: a
 * section's title ("When someone fills out a form"). A kind these words
 * don't know yet reads as its catalog label.
 */
export function kindSentence(ref: ReferenceData, kind: string): string {
  switch (kind) {
    case "cadence":
      return "On a schedule";
    case "meeting_booked":
      return "When a meeting is booked";
    case "meeting_before":
      return "Before each meeting";
    case "meeting_after":
      return "After each meeting";
    case "first_meeting":
      return "After a student's first meeting";
    case "milestone":
      return "After a number of meetings";
    case "no_meeting":
      return "When a student has had no meeting for a while";
    case "form_filled":
      return "When someone fills out a form";
    case "form_waiting":
      return "When a form is still not filled out";
    case "column_joined":
      return "When someone joins a CRM column";
    case "column_left":
      return "When someone leaves a CRM column";
    case "crm_date":
      return "Around a date";
    case "charge_failed":
      return "When a charge fails";
    case "balance_owed":
      return "When a balance is owed for a while";
    case "no_payment_method":
      return "When a family has no card or bank";
    case "credit_low":
      return "When prepaid credit runs low";
    case TASK_FINISHED:
      return "When a task is finished";
    case TASK_STATUS_CHANGED:
      return "When a task's status changes";
    default:
      return kindOf(ref, kind)?.label ?? "When something happens";
  }
}

/**
 * One automation's own trigger settings, each in a few words: which form,
 * which column, which date, the schedule, the amount, the task filters.
 * Empty for a kind with nothing to pick. A kind's own timing is never here:
 * the first steps carry it.
 */
export function triggerSettings(
  ref: ReferenceData,
  trigger: Trigger,
  automationName?: (id: string) => string | null
): string[] {
  const o = trigger.options || {};
  switch (trigger.kind) {
    case "cadence": {
      const [when, to] = cadenceWords(ref, o).split(", to ");
      return [when, `To ${to}`];
    }
    case "milestone":
      return [`${ordinal(Number(o.count) || 10)} meeting`];
    case "form_filled":
    case "form_waiting":
      return [formName(ref, o.form_id)];
    case "column_joined":
    case "column_left":
      return [columnLabel(ref, o.column)];
    case "crm_date":
      return [
        capitalize(dateFieldWords(ref, o.field)),
        ...(o.yearly ? ["Every year"] : []),
      ];
    case "balance_owed":
      return [`More than ${money(o.min_amount)}`];
    case "credit_low":
      return [`Below ${money(o.below)}`];
    case TASK_FINISHED:
    case TASK_STATUS_CHANGED: {
      const status =
        trigger.kind === TASK_STATUS_CHANGED && o.to && o.to !== "any"
          ? [`Changes to ${statusLabel(o.to)}`]
          : [];
      const filters = liveFilters(trigger.filters).map((f) =>
        filterWords(ref, f, automationName)
      );
      return [...status, ...filters];
    }
    default:
      return [];
  }
}

/**
 * The trigger as the start of a sentence, without timing: "When someone
 * fills out Family onboarding", "Before each meeting", "When a task whose
 * name contains “Check in” is finished". A kind these words don't know yet
 * reads as its catalog label.
 */
export function triggerSentence(
  ref: ReferenceData,
  trigger: Trigger,
  automationName?: (id: string) => string | null
): string {
  const o = trigger.options || {};
  switch (trigger.kind) {
    case "cadence":
      return cadenceWords(ref, o);
    case "milestone":
      return `After a student's ${ordinal(Number(o.count) || 10)} meeting`;
    case "form_filled":
      return `When someone fills out ${formName(ref, o.form_id)}`;
    case "form_waiting":
      return `When ${formName(ref, o.form_id)} is still not filled out`;
    case "column_joined":
    case "column_left": {
      // "When a student joins Proposal Sent", "When a student leaves
      // Proposal Sent", as the backend says them.
      const verb = trigger.kind === "column_joined" ? "joins" : "leaves";
      const found = columnByKey(ref, o.column);
      if (!found) return `When someone ${verb} a CRM column`;
      const name = columnInSentence(ref, found);
      return `When a ${found.column.role} ${verb} ${name}`;
    }
    case "crm_date":
      return `Around each ${dateFieldWords(ref, o.field)}`;
    case "balance_owed":
      return `When more than ${money(o.min_amount)} is owed for a while`;
    case "credit_low":
      return `When prepaid credit drops below ${money(o.below)}`;
    case TASK_FINISHED:
    case TASK_STATUS_CHANGED: {
      const live = liveFilters(trigger.filters);
      const what =
        trigger.kind === TASK_FINISHED
          ? "is finished"
          : o.to && o.to !== "any"
          ? `changes to ${statusLabel(o.to)}`
          : "changes status";
      if (!live.length) return `When a task ${what}`;
      if (live.length === 1)
        return `When a task ${filterPhrase(
          ref,
          live[0],
          automationName
        )} ${what}`;
      return `When a task ${what}, if ${namesInWords(
        live.map((f) => filterClause(ref, f, automationName))
      )}`;
    }
    default:
      return kindSentence(ref, trigger.kind);
  }
}

// ── Positions ─────────────────────────────────────────────────────────────

function amountWords(position: TimePosition) {
  return plural(position.n, position.unit === "hours" ? "hour" : "day");
}

/**
 * A first column's time in words, from the trigger: "Right away", "After
 * 3 days", "1 day before", "On the day" and "3 days after"
 * (around a date), "30 days with no meeting". timing: the kind's own
 * (null: a plain "After 3 days").
 */
export function timeWords(timing: KindTiming | null, position: TimePosition) {
  if (position.n <= 0)
    return timing?.sides === "both" ? "On the day" : "Right away";
  if (position.before)
    return `${amountWords(position)} ${timing?.before ?? "before"}`;
  return timing?.after
    ? `${amountWords(position)} ${timing.after}`
    : `After ${amountWords(position)}`;
}

/** A first column's time in words, from the trigger's own moment (timeWords). */
export function firstTimeWords(
  ref: ReferenceData,
  trigger: Trigger,
  position: TimePosition
) {
  return timeWords(kindTiming(ref, trigger.kind), position);
}

/**
 * A later column's time in words, the gap after every step in the column
 * before it finished: "Right after", "2 days later", "3 hours later".
 */
export function laterWords(position: TimePosition) {
  return position.n <= 0 ? "Right after" : `${amountWords(position)} later`;
}

/**
 * What a column has going now, leaving out what is none: "2 scheduled, 1
 * waiting for approval, 3 waiting here". Empty when it has nothing.
 */
export function columnCountsWords(counts: {
  scheduled: number;
  waiting: number;
  here: number;
}) {
  return [
    counts.scheduled > 0 && `${counts.scheduled} scheduled`,
    counts.waiting > 0 && `${counts.waiting} waiting for approval`,
    counts.here > 0 && `${counts.here} waiting here`,
  ]
    .filter(Boolean)
    .join(", ");
}

/** How "When “Intake call” ___" ends for a task's status: "is done", "is reopened", "is canceled". */
export function statusWords(ref: ReferenceData, status: string) {
  if (status === "COMPLETE") return "is done";
  return (
    ref.choices.task_statuses.find((s) => s.status === status)?.words ??
    `changes to ${statusLabel(status)}`
  );
}

/**
 * When a step happens, in words: its time in the first column ("3 days
 * after"), or in a later one ("2 days later").
 */
export function stepWhen(
  ref: ReferenceData,
  automation: { trigger: Trigger; steps: Step[] },
  step: Step
) {
  const p = step.position;
  const column = stagesOf(automation.steps).findIndex((own) =>
    own.some((x) => x.id === step.id)
  );
  return column > 0
    ? laterWords(p)
    : firstTimeWords(ref, automation.trigger, p);
}

/** "Right away", "After 3 days", "3 days before". */
export function positionLabel(position: Position): string {
  if (position.n <= 0) return "Right away";
  return position.before
    ? `${amountWords(position)} before`
    : `After ${amountWords(position)}`;
}

/** A lane task step's task by name, in quotes: "“Intake call with the parent”". */
export function laneTaskWords(
  ref: ReferenceData,
  steps: Step[],
  stepId: string | null
) {
  const step = steps.find((s) => s.id === stepId);
  return step && step.action === "task"
    ? `“${nameInWords(ref, step.task.name) || "Untitled task"}”`
    : "“a task that was taken out”";
}

/**
 * A gate card's line under its kind, for a task an earlier step makes:
 * "“Intake call”", "“Intake call” is canceled".
 */
export function laneTaskLine(
  ref: ReferenceData,
  steps: Step[],
  trigger: Trigger
) {
  const name = laneTaskWords(ref, steps, cardTaskStep(trigger));
  return trigger.kind === TASK_STATUS_CHANGED
    ? `${name} ${statusWords(ref, String(trigger.options?.to ?? ""))}`
    : name;
}

/**
 * One card of a gate as a phrase: "When “Intake call” is done", "When
 * they're in Closed Won", "When someone fills out Family onboarding".
 */
export function gateCardWords(
  ref: ReferenceData,
  automation: Lane,
  card: TriggerCard,
  boxed = false
) {
  const { trigger } = card;
  const box = boxed ? boxCardWords(ref, trigger) : null;
  if (box) return box.line;
  if (cardTaskStep(trigger) !== null) {
    const name = laneTaskWords(ref, automation.steps, cardTaskStep(trigger));
    return trigger.kind === TASK_STATUS_CHANGED
      ? `When ${name} ${statusWords(ref, String(trigger.options?.to ?? ""))}`
      : `When ${name} is done`;
  }
  if (trigger.kind === "column_joined") {
    const found = columnByKey(ref, trigger.options?.column ?? null);
    const name = found ? columnInSentence(ref, found) : "a CRM column";
    return `When they're in ${name}`;
  }
  return triggerSentence(ref, trigger);
}

/**
 * A card's title and line in an AND box of two or more, for a kind that
 * says where someone is ("In a CRM column", "In Trial"), from the backend's
 * words for it. Null for every other kind, which reads the same in any box.
 */
export function boxCardWords(ref: ReferenceData, trigger: Trigger) {
  const info = kindInfo(ref, trigger.kind);
  if (!info?.box_label || !info.box_lead) return null;
  const found = columnByKey(ref, trigger.options?.column ?? null);
  const name = found ? columnInSentence(ref, found) : "a CRM column";
  return { title: info.box_label, line: `${info.box_lead} ${name}` };
}

/**
 * A CRM column's name in a sentence, as the backend says it: "Proposal
 * Sent", or with its visualization for a built-in one's column or one whose
 * name another visualization has.
 */
function columnInSentence(
  ref: ReferenceData,
  found: NonNullable<ReturnType<typeof columnByKey>>
) {
  const twins = ref.boards
    .flatMap((board) => board.columns)
    .filter((c) => c.name === found.column.name).length;
  return found.board.builtIn || twins > 1
    ? found.column.label
    : found.column.name;
}

/** A gate in words: its cards' phrases, joined by its AND or OR. */
export function gateWords(ref: ReferenceData, automation: Lane, gate: Gate) {
  const join = gate.logic === "or" ? " or " : " and ";
  const boxed = inAndBox(gate.logic, gate.cards.length);
  return gate.cards
    .map((card, i) => {
      const words = gateCardWords(ref, automation, card, boxed);
      return i === 0 ? words : lowerFirst(words);
    })
    .join(join);
}

/** A card's own timing in words, from its kind's moment: "21 days with no meeting", "2 hours before". */
export function cardTimeWords(
  ref: ReferenceData,
  trigger: Trigger,
  time: TimePosition
) {
  return firstTimeWords(ref, trigger, time);
}

// ── Assign to and Ask first ───────────────────────────────────────────────

/** How one Assign to choice reads in a sentence: "their Algebra tutors". */
export function choicePhrase(
  ref: ReferenceData,
  choice: AssignChoice,
  assign: AssignTo,
  facets: Facets
) {
  switch (choice) {
    case "self":
      return facets.roles.length > 1 ? "the student" : "the person it is about";
    case "parents":
      return "their parents";
    case "tutors": {
      const subject = subjectName(ref, assign.tutorSubject);
      return subject ? `their ${subject} tutors` : "their tutors";
    }
    case "event_person":
      return lowerFirst(facets.eventPerson ?? "the person from the event");
    case "task_assignees":
      return "that task's assignees";
    case "admins":
      return "all admins";
    case "all_tutors": {
      const subject = subjectName(ref, assign.teachSubject);
      return subject ? `all tutors who teach ${subject}` : "all tutors";
    }
  }
}

/** A chosen group as a tag in the Assign to field: "Their Algebra tutors". */
export function choiceTag(
  ref: ReferenceData,
  choice: AssignChoice,
  assign: AssignTo,
  facets: Facets
) {
  return capitalize(choicePhrase(ref, choice, assign, facets));
}

/**
 * Who gets the task, as a sentence part: "for their tutors", "for each of
 * their tutors", "taking turns between Amy Smith and Sam Doe", or "for the
 * org's admins" when nothing is picked.
 */
export function assignPhrase(
  ref: ReferenceData,
  assign: AssignTo,
  facets: Facets
) {
  const parts = [
    ...assign.choices.map((c) => choicePhrase(ref, c, assign, facets)),
    ...assign.people.map((key) => nameOf(ref, key)),
  ];
  if (!parts.length) return `for ${ref.orgName}'s admins`;
  const several = parts.length > 1 || assign.choices.length > 0;
  if (assign.share === "turns" && several)
    return `taking turns between ${namesInWords(parts)}`;
  if (assign.share === "each" && several)
    return `for each of ${namesInWords(parts)}`;
  return `for ${namesInWords(parts)}`;
}

export function dueWords(due: { n: number; unit: "days" | "weekdays" }) {
  if (!due.n) return "due today";
  return `due in ${plural(due.n, due.unit === "weekdays" ? "weekday" : "day")}`;
}

/** Who a message goes to, as the template editor says it. */
export function sendToWords(sendTo: SendTo[]) {
  if (sendTo.includes("admins")) return "all admins";
  const family = sendTo.includes("students")
    ? "the student, with a copy to their parents"
    : sendTo.includes("parents")
    ? "their parents"
    : "";
  const tutors = sendTo.includes("tutors") ? "their tutors" : "";
  return [family, tutors].filter(Boolean).join(", and ") || "nobody";
}

/** "by email", "by text", "by email and text". */
export function channelWords(channels: { sms: boolean; email: boolean }) {
  const how = [channels.email && "email", channels.sms && "text"]
    .filter(Boolean)
    .join(" and ");
  return how ? `by ${how}` : "with no way picked to send it";
}

/** "Algebra", "Algebra and Geometry", "a subject" for one taken out. */
export function subjectNames(ref: ReferenceData, ids: number[]) {
  return (
    namesInWords(ids.map((id) => subjectName(ref, id) ?? "a subject")) ||
    "no subjects"
  );
}

/**
 * A Subjects change in words: "Add Algebra to Ava Nguyen's subjects",
 * "Removed Algebra from their subjects", "adding Algebra to …".
 */
export function subjectsWords(
  mode: SubjectsMode,
  names: string,
  whose: string,
  tense: "do" | "did" | "doing" = "do"
) {
  const verb = {
    add: { do: "Add", did: "Added", doing: "Adding" },
    remove: { do: "Remove", did: "Removed", doing: "Removing" },
  }[mode][tense];
  return `${verb} ${names} ${mode === "add" ? "to" : "from"} ${whose} subjects`;
}

/** A CRM field's value in words. */
export function fieldValueWords(
  ref: ReferenceData,
  field: CrmField | null,
  value: FieldValue
) {
  if (value === null || value === undefined || value === "") return "nothing";
  const input = field?.info.input;
  if (input === "boolean") return value ? "Yes" : "No";
  if (input === "choice")
    return (
      field?.info.options?.find((o) => o.id === value)?.name ??
      "a choice that was taken out"
    );
  if (input === "subjects" && Array.isArray(value))
    return (
      namesInWords(value.map((id) => subjectName(ref, id) ?? "a subject")) ||
      "no subjects"
    );
  if (input === "date" && typeof value === "string") return formatDate(value);
  if (typeof value === "string") return `“${value}”`;
  return String(value);
}

// ── Steps and automations ─────────────────────────────────────────────────

/**
 * One step as a sentence, without its position: "Create “Intake call with
 * {{parent.full_name}}” taking turns between all admins, due in 1 day."
 * Task names keep their {{variables}} (see variableTokens).
 */
export function stepSentence(
  ref: ReferenceData,
  trigger: Trigger,
  step: Step
): string {
  let text: string;
  switch (step.action) {
    case "message": {
      const template = templateById(ref, step.message.templateId);
      text = `Send ${template?.name ?? "a message"} to ${sendToWords(
        step.message.sendTo
      )}, ${channelWords(step.message)}`;
      break;
    }
    case "task":
      text = `Create “${step.task.name || "a task"}” ${assignPhrase(
        ref,
        step.task.assignTo,
        facetsOf(ref, trigger)
      )}, ${dueWords(step.task.due)}`;
      break;
    case "crm_move": {
      const found = columnById(
        ref,
        step.crmMove.boardId,
        step.crmMove.columnId
      );
      text = found
        ? `Move them to ${found.column.label}`
        : "Move them to a CRM column";
      break;
    }
    case "crm_field": {
      const config = step.crmField;
      if (isSubjects(config)) {
        text = subjectsWords(
          config.mode,
          subjectNames(ref, config.subject_ids),
          "their"
        );
        break;
      }
      const field = fieldByRef(ref, config.field);
      text = field
        ? `Set ${field.label} to ${fieldValueWords(ref, field, config.value)}`
        : "Update a CRM field";
      break;
    }
    case "close_task":
      text =
        step.closeTask.status === TaskStatus.CANCELLED
          ? "Cancel the task"
          : "Mark the task done";
      break;
    case "assign_tutor":
      text = tutorSentence(ref, step.assignTutor);
      break;
    case "add_note":
      text = step.note.text.trim()
        ? `Add a note: ${noteWords(ref, step.note.text)}`
        : "Add a note";
      break;
  }
  if (step.askFirst.on)
    text = `Ask ${
      APPROVER_ONE[step.askFirst.approvers ?? "admins"]
    } first, then ${lowerFirst(text)}`;
  return `${text}.`;
}

/** A short title for a step's card: the task's name in words, the template's name, the column or the field. */
export function stepTitle(ref: ReferenceData, step: Step) {
  switch (step.action) {
    case "message":
      return (
        templateById(ref, step.message.templateId)?.name ?? "No template picked"
      );
    case "task":
      return nameInWords(ref, step.task.name) || "Untitled task";
    case "crm_move": {
      const found = columnById(
        ref,
        step.crmMove.boardId,
        step.crmMove.columnId
      );
      return found ? `Move to ${found.column.name}` : "Move to a CRM column";
    }
    case "crm_field":
      return isSubjects(step.crmField)
        ? "Update subjects"
        : `Update ${
            fieldByRef(ref, step.crmField.field)?.label ?? "a CRM field"
          }`;
    case "close_task":
    case "assign_tutor":
    case "add_note":
      return ACTION_LABELS[step.action];
  }
}

/** A name for an automation from its first step, as a new one is named. */
export function suggestName(
  ref: ReferenceData,
  automation: { trigger: Trigger; steps: Step[] }
) {
  const first = automation.steps[0];
  if (first) return stepTitle(ref, first);
  return kindOf(ref, automation.trigger.kind)?.label ?? "New automation";
}

/** The whole automation in plain words: its trigger, then each step at its position. */
export function automationSentence(
  ref: ReferenceData,
  automation: { trigger: Trigger; steps: Step[] },
  automationName?: (id: string) => string | null
) {
  const when = triggerSentence(ref, automation.trigger, automationName);
  if (!automation.steps.length) return `${when}, nothing happens yet.`;
  const steps = automation.steps.map(
    (step) =>
      `${stepWhen(ref, automation, step)}: ${lowerFirst(
        stepSentence(ref, automation.trigger, step)
      )}`
  );
  return `${when}. ${steps.map(capitalize).join(" ")}`;
}

// ── Close a task, Assign a tutor, Add a note ──────────────────────────────

/** Who Assign a tutor chooses from: "All tutors", "Tutors who teach Algebra", or the tutors' names. */
export function tutorPoolWords(ref: ReferenceData, config: AssignTutorConfig) {
  switch (config.choose) {
    case "all":
      return "All tutors";
    case "subject":
      return config.subject === null
        ? "Tutors who teach a subject"
        : `Tutors who teach ${
            subjectName(ref, config.subject) ?? "a subject that was taken out"
          }`;
    case "specific":
      return (
        namesInWords(config.tutors.map((key) => nameOf(ref, key))) ||
        "No tutors picked"
      );
  }
}

/** How Assign a tutor gives them out: "Taking turns" or "All of them". */
export function tutorShareWords(config: AssignTutorConfig) {
  return config.share === "turns" ? "Taking turns" : "All of them";
}

/** Assign a tutor as a sentence: "Assign a tutor who teaches Algebra, taking turns". */
export function tutorSentence(ref: ReferenceData, config: AssignTutorConfig) {
  const turns = config.share === "turns";
  switch (config.choose) {
    case "all":
      return turns ? "Assign a tutor, taking turns" : "Assign every tutor";
    case "subject": {
      const subject =
        config.subject === null
          ? "a subject"
          : subjectName(ref, config.subject) ?? "a subject that was taken out";
      return turns
        ? `Assign a tutor who teaches ${subject}, taking turns`
        : `Assign every tutor who teaches ${subject}`;
    }
    case "specific": {
      const names = config.tutors.map((key) => nameOf(ref, key));
      if (!names.length) return "Assign a tutor";
      if (!turns || names.length === 1) return `Assign ${namesInWords(names)}`;
      return `Assign ${names.slice(0, -1).join(", ")} or ${
        names[names.length - 1]
      }, taking turns`;
    }
  }
}

/** A note's text in words, its variables said plainly, cut to about a line of a card. */
export function noteWords(ref: ReferenceData, text: string, most = 80) {
  const words = nameInWords(ref, text);
  if (words.length <= most) return words;
  const cut = words.slice(0, most);
  const space = cut.lastIndexOf(" ");
  return `${(space > most / 2 ? cut.slice(0, space) : cut).trimEnd()}…`;
}
